/* GoogleSyncPanel — super-admin only.
 *
 * Renders one of two states based on /status:
 *   • not configured → step-by-step setup guide + per-check status grid
 *     so the user knows exactly which env var or share step is missing.
 *   • configured → operational dashboard with last-refresh timestamps,
 *     "Refresh Sheets now" button, "Push tonight's backup to Drive"
 *     button, and a link to the master spreadsheet.
 *
 * Every action is idempotent + fail-safe — clicking Refresh on an
 * unconfigured system just bounces back the same "not configured"
 * message, no error toast.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Cloud, ExternalLink, Loader2, RefreshCw, CheckCircle2,
  AlertTriangle, Copy, FileText, FolderOpen, Settings,
  ChevronDown, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/google-sync`;

const fmtTs = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const Pill = ({ ok, label }) => (
  <span data-testid={`gs-pill-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold
    ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-700'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
    {label}
  </span>
);

const Copyable = ({ text }) => {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button onClick={() => {
      navigator.clipboard.writeText(text);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }}
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5
                 border border-[#E5E7EB] rounded font-mono hover:bg-[#FAFBFC]"
      data-testid={`gs-copy-${text.slice(0, 8)}`}>
      <Copy className="w-2.5 h-2.5" />
      {copied ? 'Copied!' : text}
    </button>
  );
};

const GoogleSyncPanel = () => {
  const [status, setStatus] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [s, i] = await Promise.all([
        axios.get(`${API}/status`, { withCredentials: true }),
        axios.get(`${API}/setup-instructions`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setInstructions(i.data);
      // Auto-collapse instructions if everything is green
      if (s.data.configured && s.data.drive_ready) {
        setShowInstructions(false);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load Google sync status.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefreshSheets = async () => {
    setRefreshing(true); setRefreshResult(null); setError('');
    try {
      const r = await axios.post(`${API}/sheets/refresh-now`, {},
                                    { withCredentials: true });
      setRefreshResult(r.data);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Sheets refresh failed.');
    }
    setRefreshing(false);
  };

  if (loading) {
    return <div className="flex justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
    </div>;
  }
  if (!status) return null;

  const checks = status.checks || {};

  return (
    <div className="space-y-4" data-testid="google-sync-panel">
      {/* Header */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-bold text-base text-[#0A0A0A]
                          inline-flex items-center gap-2">
              <Cloud className="w-4 h-4 text-[#BA1826]" />
              Google Drive &amp; Sheets Sync
            </h2>
            <p className="text-[11px] text-[#4B5563] mt-1 max-w-2xl">
              Mirrors uploaded partnership documents to a shared Google
              Drive folder and refreshes a master spreadsheet of
              organizations, contacts, interactions, and opportunities
              every morning. Off-pod backup tarballs are uploaded after
              every nightly snapshot.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Pill ok={status.configured} label="Service account" />
            <Pill ok={status.drive_ready} label="Drive" />
            <Pill ok={status.sheets_ready} label="Sheets" />
            <button onClick={load}
              className="text-xs px-2.5 py-1.5 text-[#4B5563]
                        hover:text-[#0A0A0A] inline-flex items-center gap-1"
              data-testid="gs-refresh-status">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700
                       text-xs p-2 rounded inline-flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {error}
        </div>
      )}

      {/* Per-check status grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3"
        data-testid="gs-checks-grid">
        {Object.entries(checks).map(([key, c]) => (
          <div key={key}
            className={`border rounded p-3 ${c.ok
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-amber-50 border-amber-200'}`}
            data-testid={`gs-check-${key}`}>
            <div className="flex items-center gap-1.5">
              {c.ok
                ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                : <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />}
              <p className="text-[10px] uppercase tracking-wider font-bold
                          opacity-80">
                {key.replace(/_/g, ' ')}
              </p>
            </div>
            <p className="text-[12px] text-[#0A0A0A] mt-1.5 leading-snug">
              {c.detail}
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-[10px] text-[#4B5563]">
                <span className="font-bold">Env:</span>{' '}
                <code className="font-mono text-[10px]">{c.env_var}</code>
              </p>
              <p className="text-[10px] text-[#4B5563] truncate">
                <span className="font-bold">Current:</span>{' '}
                <code className="font-mono text-[10px]">
                  {c.current_value}
                </code>
              </p>
              {c.service_account_email && (
                <p className="text-[10px] text-[#4B5563]">
                  <span className="font-bold">SA email:</span>{' '}
                  <Copyable text={c.service_account_email} />
                </p>
              )}
              {c.spreadsheet_url && (
                <a href={c.spreadsheet_url} target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-[#BA1826] hover:underline
                            inline-flex items-center gap-1"
                  data-testid="gs-open-sheet">
                  Open spreadsheet <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Operational dashboard — visible when fully configured */}
      {status.configured && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4"
          data-testid="gs-ops-panel">
          <h3 className="text-xs font-bold uppercase tracking-wider
                         text-[#0A0A0A] mb-3">
            Operations
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Sheets card */}
            <div className="border border-[#E5E7EB] rounded p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-bold inline-flex items-center
                              gap-1">
                    <FileText className="w-3 h-3" /> Partnerships master sheet
                  </p>
                  <p className="text-[10px] text-[#4B5563] mt-0.5">
                    Last refreshed: {fmtTs(status.last_sheets_refresh_at)}
                  </p>
                  {status.last_sheets_row_counts && (
                    <p className="text-[10px] text-[#4B5563] mt-0.5">
                      Orgs: {status.last_sheets_row_counts.organizations} ·
                      Contacts: {status.last_sheets_row_counts.contacts} ·
                      Interactions: {status.last_sheets_row_counts.interactions} ·
                      Opportunities: {status.last_sheets_row_counts.opportunities}
                    </p>
                  )}
                  {status.last_sheets_error && (
                    <p className="text-[10px] text-rose-700 mt-1">
                      Last error: {status.last_sheets_error}
                    </p>
                  )}
                </div>
                <button onClick={handleRefreshSheets} disabled={refreshing}
                  className="text-xs px-3 py-1.5 bg-[#BA1826] text-white
                            rounded inline-flex items-center gap-1.5
                            disabled:opacity-60"
                  data-testid="gs-refresh-sheets">
                  {refreshing
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <RefreshCw className="w-3 h-3" />}
                  Refresh now
                </button>
              </div>
              {refreshResult?.ok && (
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200
                              rounded text-[11px] text-emerald-800"
                  data-testid="gs-refresh-result">
                  ✓ Sheet refreshed.
                  {refreshResult.auto_created
                    && ' Spreadsheet was auto-created in your shared folder.'}
                  {' '}
                  <a href={refreshResult.url} target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold underline">
                    Open it
                  </a>
                </div>
              )}
              {status.master_spreadsheet_url && (
                <a href={status.master_spreadsheet_url} target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-[11px]
                            text-[#BA1826] hover:underline">
                  Open master spreadsheet
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>

            {/* Drive card */}
            <div className="border border-[#E5E7EB] rounded p-3">
              <p className="text-xs font-bold inline-flex items-center
                          gap-1">
                <FolderOpen className="w-3 h-3" /> Drive backup mirror
              </p>
              <p className="text-[10px] text-[#4B5563] mt-0.5">
                {status.drive_ready
                  ? `Last upload: ${fmtTs(status.last_drive_backup_at)}`
                  : 'Drive root folder not configured yet.'}
              </p>
              {status.last_drive_backup_date && (
                <p className="text-[10px] text-[#4B5563] mt-0.5">
                  Snapshot date: <code>{status.last_drive_backup_date}</code>
                </p>
              )}
              {status.last_drive_backup_error && (
                <p className="text-[10px] text-rose-700 mt-1">
                  Last error: {status.last_drive_backup_error}
                </p>
              )}
              <p className="text-[10px] text-[#4B5563] mt-2 italic">
                Nightly snapshots auto-upload to your shared Drive folder
                under <code>/Backups/</code>. No manual action needed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Setup instructions — always available, collapsed when ready */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg">
        <button onClick={() => setShowInstructions(!showInstructions)}
          className="w-full px-4 py-3 flex items-center justify-between
                    text-left hover:bg-[#FAFBFC]"
          data-testid="gs-toggle-instructions">
          <span className="text-xs font-bold uppercase tracking-wider
                         text-[#0A0A0A] inline-flex items-center gap-2">
            <Settings className="w-3.5 h-3.5" />
            Setup instructions
            {!status.configured && (
              <span className="text-[10px] font-normal normal-case
                              text-rose-700 ml-1">
                — required to enable sync
              </span>
            )}
          </span>
          {showInstructions
            ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />
            : <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />}
        </button>
        {showInstructions && instructions && (
          <div className="px-4 pb-4 space-y-3 border-t border-[#E5E7EB]
                         pt-3" data-testid="gs-instructions-body">
            {instructions.service_account_email && (
              <div className="bg-sky-50 border border-sky-200 rounded p-2.5
                            text-[11px] text-sky-900">
                <strong>Your service-account email:</strong>{' '}
                <Copyable text={instructions.service_account_email} />
                {' '}— share your Drive folder + spreadsheet with this
                address as <strong>Editor</strong>.
              </div>
            )}
            {instructions.steps.map((s, i) => (
              <div key={i} className="border-l-2 border-[#BA1826] pl-3 py-1"
                data-testid={`gs-step-${i + 1}`}>
                <p className="text-xs font-bold text-[#0A0A0A]">
                  {s.title}
                </p>
                <p className="text-[11px] text-[#4B5563] mt-1 leading-relaxed
                            whitespace-pre-wrap">
                  {s.body}
                </p>
              </div>
            ))}
            <div className="bg-amber-50 border border-amber-200 rounded p-2.5
                          text-[11px] text-amber-900">
              <strong>Where to put env vars:</strong>{' '}
              edit <code>/app/backend/.env</code> and add the lines, then
              run <code>sudo supervisorctl restart backend</code> from the
              pod terminal (or rely on hot reload after saving the file).
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleSyncPanel;
